<template>
  <v-dialog v-model="dialog" persistent max-width="900" class="action-dialog">
    <v-card elevation="12" class="dialog-card">
      <v-card-title class="dialog-header">
        <div class="d-flex align-center justify-space-between w-100">
          <h3 class="dialog-title mb-0">{{ headerText }}</h3>
          <v-btn icon class="close-btn" @click="toggleModal" aria-label="Close">
            <v-icon color="#999" size="20">mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>
      <v-card-text class="dialog-content">
        <v-stepper v-model="step" class="custom-stepper" elevation="0">
          <v-stepper-header class="stepper-header mt-4">
            <v-stepper-step
              :complete="step > 1"
              step="1"
              class="custom-step"
              color="primary"
              @click="navigateToStep(1)"
            >
              Basic Info
              <small class="step-description"
                >Organization & event settings</small
              >
            </v-stepper-step>
            <v-divider class="step-divider"></v-divider>
            <v-stepper-step
              :complete="step > 2"
              step="2"
              class="custom-step"
              color="primary"
              @click="navigateToStep(2)"
            >
              Advanced
              <small class="step-description">Additional configuration</small>
            </v-stepper-step>
          </v-stepper-header>
          <v-stepper-items class="stepper-content">
            <v-stepper-content step="1" class="step-content">
              <StepBasicInfo
                ref="basicStep"
                :value="basicFields"
                :valid.sync="valid"
                @next="goToNextStep"
                @close="toggleModal"
                @clearAdvancedStepStatusValueOnActionEventChange="
                  clearAdvancedStepStatusValueOnActionEventChange
                "
              />
            </v-stepper-content>
            <v-stepper-content step="2" class="step-content">
              <StepAdvanced
                ref="advancedStep"
                :value="advanceInfo"
                :valid.sync="valid"
                :action-event="basicFields.action_event"
                @submit="submitForm"
                @previous="goToPreviousStep"
                @close="toggleModal"
              />
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card-text>
      <v-card-actions
        class="pa-4"
        style="position: sticky; bottom: 0; background: #fff"
      >
        <v-row align="center" no-gutters class="w-100">
          <v-col
            :cols="step === 2 ? 10 : 12"
            class="d-flex justify-content-between px-2"
          >
            <div>
              <v-btn
                v-show="step === 2"
                class="mr-3"
                outlined
                @click="goToPreviousStep()"
              >
                Previous
              </v-btn>
            </div>
            <div>
              <v-btn
                v-show="step === 2"
                color="primary"
                @click="$refs.advancedStep.handleSubmit()"
                >Submit</v-btn
              >
            </div>
            <div>
              <v-btn
                v-show="step === 1"
                color="primary"
                @click="$refs.basicStep.handleNext()"
                >Next</v-btn
              >
            </div>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { SET_ACTIONS_DIALOG_SERVER_DATA } from "@/core/services/store/webhooks.module";
import StepBasicInfo from "@/own/components/datahub/webhooks/StepBasicInfo.vue";
import StepAdvanced from "@/own/components/datahub/webhooks/StepAdvanced.vue";
import swalEdited from "@/core/services/AlertServices/SwalEdited";
import swalAdded from "@/core/services/AlertServices/SwalAdded";

export default {
  name: "ActionsDialogNew",
  props: ["refresher", "pageLoader"],
  components: { StepBasicInfo, StepAdvanced },
  data() {
    return {
      dialog: false,
      step: 1,
      valid: false,
      basicFields: {
        organization_id: null,
        apply_type_id: null,
        status: null,
        maximum_retry: null,
        action_event: null,
      },
      advanceInfo: {},
    };
  },
  computed: {
    actionItem() {
      return this.$store.getters.getItemForAction?.id || null;
    },
    headerText() {
      if (this.actionItem) return "Edit Webhook";

      return "Add Webhook";
    },
  },
  methods: {
    clearAdvancedStepStatusValueOnActionEventChange() {
      this.$refs.advancedStep.clearActionStatuses();
    },
    async toggleModal() {
      if (this.dialog) {
        this.$refs.basicStep && this.$refs.basicStep.reset();
        this.$refs.advancedStep && this.$refs.advancedStep.reset();
        this.dialog = false;
      } else {
        await this.loadServerData();
        this.step = 1;
        this.dialog = true;
      }
    },
    goToNextStep(formFields) {
      this.basicFields = { ...formFields };
      this.step = 2;
    },
    navigateToStep(target) {
      if (target === 2) {
        const basicComp = this.$refs.basicStep;

        if (basicComp && basicComp.isFormValid) {
          basicComp.$refs.form.validate();
          setTimeout(() => {
            if (basicComp.isFormValid) {
              this.basicFields = { ...basicComp.formData };
              this.step = 2;
            }
          }, 50);
        }
      } else {
        this.step = target;
      }
    },
    async submitForm(advancedData = {}) {
      // combine basic and advanced fields into one payload
      const payload = {
        ...this.basicFields,
        ...advancedData,
      };

      // determine action url
      const actionUrl = this.actionItem ? "update" : "store";

      if (this.actionItem) {
        payload.id = this.actionItem;
      }

      try {
        this.pageLoader(true);
        await ApiService.post(`/datahub/webhooks/${actionUrl}`, payload);
        if (this.actionItem) swalEdited();
        else swalAdded();
        this.toggleModal();
        this.refresher && this.refresher();
      } catch (err) {
        // keep loader off on error
        this.pageLoader(false);
      }
    },
    goToPreviousStep() {
      this.step = 1;
    },
    async loadServerData() {
      this.pageLoader(true);

      const action = this.actionItem ? "edit" : "create";
      let url = `/datahub/webhooks/${action}`;
      let payload = {};

      if (action === "edit") {
        payload.id = this.actionItem;
      }

      ApiService.post(url, payload)
        .then(({ data }) => {
          this.$store.commit(SET_ACTIONS_DIALOG_SERVER_DATA, {
            ...data,
          });

          // If editing an existing action, populate local form state so children receive values
          if (action === "edit" && data && data.webhook) {
            const a = data.webhook;
            // fill known basic fields (use fallbacks)
            this.basicFields = {
              organization_id: a.organization_id ?? null,
              apply_type_id: a.apply_type_id ?? null,
              status: a.status ?? null,
              maximum_retry: a.maximum_retry ?? null,
              action_event: a.action_event ?? null,
              url: a.url ?? null,
              customer_id: a.customer_id ?? null,
            };

            // Put other keys into advanceInfo so StepAdvanced can pick them up
            const basicKeys = Object.keys(this.basicFields);
            const adv = {};
            Object.keys(a).forEach((k) => {
              if (!basicKeys.includes(k)) adv[k] = a[k];
            });

            this.advanceInfo = { ...adv };
          }

          this.pageLoader(false);
        })
        .catch(() => {
          this.dialog = false;
          this.pageLoader(false);
        });
    },
  },
};
</script>

<style scoped>
.action-dialog {
  backdrop-filter: blur(4px);
}

.dialog-card {
  /* make dialog a fixed-height column so header and footer remain fixed */
  display: flex;
  flex-direction: column;
  height: 60vh;
  max-height: 60vh;
  border-radius: 16px;
  overflow: hidden;
}

.dialog-header {
  background: #ffffff;
  color: #333;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
}

.dialog-title {
  font-size: 20px;
  font-weight: 600;
  color: #2d3748;
}

.close-btn {
  background: transparent;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.05);
}

.dialog-content {
  /* allow the body to scroll while keeping header/footer fixed */
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 24px;
  background: #fafbfc;
}

.custom-stepper {
  background: transparent;
  box-shadow: none;
}

.stepper-header {
  background: white;
  border-radius: 8px;
  padding: 16px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  border: 1px solid #e2e8f0;
}

.custom-step {
  padding: 8px 12px;
  cursor: pointer;
}

.step-description {
  color: #718096;
  font-size: 11px;
  margin-top: 2px;
  display: block;
  font-weight: normal;
}

.step-divider {
  margin: 0 16px;
  opacity: 0.3;
}

.stepper-content {
  overflow: visible;
}

.step-content {
  padding: 0;
}

.v-card-actions {
  /* keep footer visible and above the scrollable content */
  position: sticky;
  bottom: 0;
  background: #fff;
  z-index: 10;
}

.v-stepper__step {
  min-width: 120px;
}

.v-stepper__step--active .v-stepper__label {
  color: #667eea;
  font-weight: 600;
}

.v-stepper__step--complete .v-stepper__label {
  color: #48bb78;
}
</style>
